/**
 * HUD layout editor — F2 toggles drag-positioning mode for the v10 main plate.
 *
 * Why this exists: the painted-chrome PNG has bezel positions that don't match
 * the natural flex layout of the DOM children. Eyeballing % offsets blind is
 * tedious and lossy. With this tool, each child group becomes draggable in-page
 * and a debug overlay shows live coords. User drags onto the painted bezels,
 * reads the resulting top%/left%/width%/height% values from the overlay, and
 * those values get baked into style.css as the final positions.
 *
 * Activation: press F2. Press F2 again to exit (children stay where you put them
 * but revert to flex on next page load — coords are debug output, not persisted).
 *
 * Selectors edited in plate-edit mode (relative to .mp-plate):
 *   .mp-portrait        - portrait + XP ring + level badge
 *   .mp-equip-grid      - 3 equipment slots row
 *   .mp-skills-wrap     - gold sub-panel containing Q/W/E/R/Z
 *   .mp-backpack        - 6×2 backpack grid
 *
 * Selectors edited in bars-strip mode (relative to .mp-bars-strip):
 *   .mp-orb-warmth      - warmth orb + value
 *   .mp-bars-stack      - HP/Mana/Stam stack
 *   .mp-orb-hunger      - hunger orb + value
 */

interface DraggableGroup {
  selector: string;       // display-only label (shown in overlay)
  cssSelector: string;    // valid CSS selector emitted by generateCss()
  el: HTMLElement;
  parent: HTMLElement;
  label: string;
  /** Original CSS so we can restore on exit. */
  saved: { position: string; left: string; top: string; width: string; height: string };
  /** Resize handles attached during F2 mode — removed on exit. */
  handles: HTMLElement[];
}

type HandleDir = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";
const HANDLE_DIRS: HandleDir[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

interface ResizeOp {
  group: DraggableGroup;
  dir: HandleDir;
  startMouseX: number;
  startMouseY: number;
  startLeftPct: number;
  startTopPct: number;
  startWidthPct: number;
  startHeightPct: number;
  parentWidthPx: number;
  parentHeightPx: number;
}

const PLATE_GROUPS: Array<{ selector: string; label: string }> = [
  { selector: ".mp-portrait",    label: "Portrait" },
  { selector: ".mp-equip-grid",  label: "Equip row" },
  { selector: ".mp-skills-wrap", label: "Abilities (Q/W/E/R/Z)" },
  { selector: ".mp-backpack",    label: "Backpack 6×2" },
];

const BARS_GROUPS: Array<{ selector: string; label: string }> = [
  { selector: ".mp-orb-warmth", label: "Warmth orb" },
  { selector: ".mp-bar-hp",     label: "HP bar" },
  { selector: ".mp-bar-mana",   label: "Mana bar" },
  { selector: ".mp-bar-stam",   label: "Stamina bar" },
  { selector: ".mp-orb-hunger", label: "Hunger orb" },
];

/** Per-group cell registries — when F2 is on, each cell becomes its own
 *  draggable nested under its parent. Used to align individual circles /
 *  bezels against the painted asset. (Bars are now top-level under
 *  .mp-bars-strip — no nested stack.) */
const CELL_PARENTS: Array<{ parentSelector: string; cellSelector: string; labelPrefix: string }> = [
  { parentSelector: ".mp-equip-grid",  cellSelector: ".mp-cell-equip",    labelPrefix: "Equip" },
  { parentSelector: ".mp-skills-wrap", cellSelector: ".mp-skill",         labelPrefix: "Ability" },
  { parentSelector: ".mp-backpack",    cellSelector: ".mp-cell-backpack", labelPrefix: "Bag" },
];

let active = false;
let groups: DraggableGroup[] = [];
let overlay: HTMLDivElement | null = null;
let dragging: DraggableGroup | null = null;
let dragOffsetX = 0;
let dragOffsetY = 0;
let resizing: ResizeOp | null = null;

export function installLayoutEditor(): void {
  document.addEventListener("keydown", (e) => {
    if (e.key === "F2" && !(e.target instanceof HTMLInputElement)) {
      e.preventDefault();
      active ? exit() : enter();
    }
  });
}

function enter(): void {
  active = true;
  groups = [];

  const plate = document.querySelector(".mp-plate") as HTMLElement | null;
  const bars = document.querySelector(".mp-bars-strip") as HTMLElement | null;
  if (!plate || !bars) {
    console.error("[layout-editor] .mp-plate or .mp-bars-strip not found");
    return;
  }

  // Make sure parents are positioning contexts (they should be already).
  if (getComputedStyle(plate).position === "static") plate.style.position = "relative";
  if (getComputedStyle(bars).position === "static") bars.style.position = "relative";

  // Collapse the wrapper sections + dividers so absolute-positioned children
  // can land anywhere on .mp-plate without flex pushing them around.
  ([".mp-section-left", ".mp-section-mid", ".mp-section-right"] as const).forEach((sel) => {
    const el = document.querySelector(sel) as HTMLElement | null;
    if (el) el.style.display = "contents";
  });

  // Promote each draggable group to position:absolute, snap to its current
  // bounding-box position so it doesn't visually jump on enter.
  for (const { selector, label } of PLATE_GROUPS) registerGroup(selector, label, plate);
  for (const { selector, label } of BARS_GROUPS)  registerGroup(selector, label, bars);

  // Per-cell granular drag: for each parent that holds repeated cells (equip
  // slots, ability slots, backpack cells, bars), promote each cell to its
  // own draggable absolute element. The cell's positioning context is its
  // parent group, so dragging a cell only moves it within the group bounds.
  for (const cp of CELL_PARENTS) {
    const parentEl = document.querySelector(cp.parentSelector) as HTMLElement | null;
    if (!parentEl) continue;
    if (getComputedStyle(parentEl).position === "static") parentEl.style.position = "relative";
    const cells = Array.from(parentEl.querySelectorAll(cp.cellSelector)) as HTMLElement[];
    // Two-pass capture: take all cell bboxes BEFORE promoting any of them, so
    // promoting cell N doesn't reflow cells N+1.. (they're still under flex/grid
    // when measured).
    const parentBox = parentEl.getBoundingClientRect();
    const cellBoxes = cells.map((c) => c.getBoundingClientRect());
    cells.forEach((cell, i) => {
      const css = `${cp.parentSelector} > ${cp.cellSelector}:nth-child(${i + 1})`;
      registerGroupWithBbox(
        `${cp.labelPrefix} ${i + 1}`, `${cp.labelPrefix} ${i + 1}`,
        parentEl, cell, true, css, parentBox, cellBoxes[i]!,
      );
    });
  }

  buildOverlay();
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);
  console.log("[layout-editor] ON — drag groups onto painted bezels. F2 to exit.");
}

function exit(): void {
  active = false;
  document.removeEventListener("mousemove", onMouseMove);
  document.removeEventListener("mouseup", onMouseUp);
  // Restore each group's CSS to whatever the stylesheet says (left blank so
  // baked rules from style.css apply). Strip resize handles.
  for (const g of groups) {
    for (const h of g.handles) h.remove();
    g.el.style.position = g.saved.position;
    g.el.style.left = g.saved.left;
    g.el.style.top = g.saved.top;
    g.el.style.width = g.saved.width;
    g.el.style.height = g.saved.height;
    g.el.style.cursor = "";
    g.el.classList.remove("mp-layout-edit");
    g.el.removeEventListener("mousedown", onMouseDown as EventListener);
  }
  groups = [];
  ([".mp-section-left", ".mp-section-mid", ".mp-section-right"] as const).forEach((sel) => {
    const el = document.querySelector(sel) as HTMLElement | null;
    if (el) el.style.display = "";
  });
  overlay?.remove();
  overlay = null;
  console.log("[layout-editor] OFF — final positions printed above.");
}

function registerGroup(
  selector: string,
  label: string,
  parent: HTMLElement,
  el?: HTMLElement,
  isCell: boolean = false,
  cssSelector?: string,
): void {
  if (!el) {
    el = document.querySelector(selector) as HTMLElement | null ?? undefined;
    if (!el) {
      console.warn(`[layout-editor] missing selector: ${selector}`);
      return;
    }
  }
  registerGroupWithBbox(
    selector, label, parent, el, isCell, cssSelector,
    parent.getBoundingClientRect(), el.getBoundingClientRect(),
  );
}

function registerGroupWithBbox(
  selector: string,
  label: string,
  parent: HTMLElement,
  el: HTMLElement,
  isCell: boolean,
  cssSelector: string | undefined,
  parentBox: DOMRect,
  elBox: DOMRect,
): void {
  // For top-level groups, the display selector IS the css selector. Cells
  // pass an explicit `cssSelector` (e.g. `.mp-equip-grid > .mp-cell-equip:nth-child(2)`).
  const effectiveCssSelector = cssSelector ?? selector;
  const saved = {
    position: el.style.position,
    left: el.style.left,
    top: el.style.top,
    width: el.style.width,
    height: el.style.height,
  };
  // Pin in place at current visual position (relative to parent).
  el.style.position = "absolute";
  el.style.left = `${((elBox.left - parentBox.left) / parentBox.width) * 100}%`;
  el.style.top = `${((elBox.top - parentBox.top) / parentBox.height) * 100}%`;
  el.style.width = `${(elBox.width / parentBox.width) * 100}%`;
  el.style.height = `${(elBox.height / parentBox.height) * 100}%`;
  el.style.cursor = "move";
  el.classList.add("mp-layout-edit");
  if (isCell) el.classList.add("mp-layout-edit-cell");
  el.addEventListener("mousedown", onMouseDown as EventListener);

  // Spawn 8 resize handles (corners + edges) inside the element. They
  // overlay on the group's bbox edges; mousedown on a handle initiates
  // resize (and stops the drag handler).
  const handles: HTMLElement[] = [];
  for (const dir of HANDLE_DIRS) {
    const h = document.createElement("div");
    h.className = `mp-resize-handle h-${dir}`;
    if (isCell) h.classList.add("mp-resize-handle-cell");
    h.dataset.dir = dir;
    h.addEventListener("mousedown", (ev) => onHandleMouseDown(ev, dir));
    el.appendChild(h);
    handles.push(h);
  }

  groups.push({ selector, cssSelector: effectiveCssSelector, el, parent, label, saved, handles });
}

function onHandleMouseDown(ev: MouseEvent, dir: HandleDir): void {
  ev.preventDefault();
  ev.stopPropagation(); // don't trigger drag on the parent
  const handle = ev.currentTarget as HTMLElement;
  const el = handle.parentElement as HTMLElement;
  const g = groups.find((g) => g.el === el);
  if (!g) return;
  const parentBox = g.parent.getBoundingClientRect();
  resizing = {
    group: g,
    dir,
    startMouseX: ev.clientX,
    startMouseY: ev.clientY,
    startLeftPct: parseFloat(el.style.left) || 0,
    startTopPct: parseFloat(el.style.top) || 0,
    startWidthPct: parseFloat(el.style.width) || 0,
    startHeightPct: parseFloat(el.style.height) || 0,
    parentWidthPx: parentBox.width,
    parentHeightPx: parentBox.height,
  };
}

function onMouseDown(ev: MouseEvent): void {
  const el = ev.currentTarget as HTMLElement;
  const g = groups.find((g) => g.el === el);
  if (!g) return;
  ev.preventDefault();
  ev.stopPropagation();
  dragging = g;
  const elBox = g.el.getBoundingClientRect();
  dragOffsetX = ev.clientX - elBox.left;
  dragOffsetY = ev.clientY - elBox.top;
}

function onMouseMove(ev: MouseEvent): void {
  if (resizing) {
    ev.preventDefault();
    applyResize(resizing, ev);
    refreshOverlay();
    return;
  }
  if (!dragging) return;
  ev.preventDefault();
  const parentBox = dragging.parent.getBoundingClientRect();
  const newLeftPx = ev.clientX - parentBox.left - dragOffsetX;
  const newTopPx  = ev.clientY - parentBox.top  - dragOffsetY;
  dragging.el.style.left = `${(newLeftPx / parentBox.width) * 100}%`;
  dragging.el.style.top  = `${(newTopPx  / parentBox.height) * 100}%`;
  refreshOverlay();
}

function onMouseUp(_ev: MouseEvent): void {
  dragging = null;
  resizing = null;
}

function applyResize(op: ResizeOp, ev: MouseEvent): void {
  const dxPct = ((ev.clientX - op.startMouseX) / op.parentWidthPx) * 100;
  const dyPct = ((ev.clientY - op.startMouseY) / op.parentHeightPx) * 100;
  let newLeft = op.startLeftPct;
  let newTop = op.startTopPct;
  let newWidth = op.startWidthPct;
  let newHeight = op.startHeightPct;
  // West-side handles: changing left also changes width (right edge stays fixed).
  if (op.dir === "nw" || op.dir === "w" || op.dir === "sw") {
    newLeft = op.startLeftPct + dxPct;
    newWidth = op.startWidthPct - dxPct;
  }
  // East-side handles: width grows, left unchanged.
  if (op.dir === "ne" || op.dir === "e" || op.dir === "se") {
    newWidth = op.startWidthPct + dxPct;
  }
  // North-side handles: changing top also changes height (bottom edge stays fixed).
  if (op.dir === "nw" || op.dir === "n" || op.dir === "ne") {
    newTop = op.startTopPct + dyPct;
    newHeight = op.startHeightPct - dyPct;
  }
  // South-side handles: height grows, top unchanged.
  if (op.dir === "sw" || op.dir === "s" || op.dir === "se") {
    newHeight = op.startHeightPct + dyPct;
  }
  // Clamp minimums so the group doesn't collapse to nothing.
  if (newWidth < 0.5) newWidth = 0.5;
  if (newHeight < 0.5) newHeight = 0.5;
  op.group.el.style.left = `${newLeft.toFixed(3)}%`;
  op.group.el.style.top = `${newTop.toFixed(3)}%`;
  op.group.el.style.width = `${newWidth.toFixed(3)}%`;
  op.group.el.style.height = `${newHeight.toFixed(3)}%`;
}

function buildOverlay(): void {
  overlay = document.createElement("div");
  overlay.id = "mp-layout-overlay";
  Object.assign(overlay.style, {
    position: "fixed",
    top: "12px",
    right: "12px",
    width: "380px",
    maxHeight: "90vh",
    overflowY: "auto",
    padding: "12px 14px",
    background: "rgba(8,10,14,0.95)",
    border: "1px solid #f7c360",
    borderRadius: "6px",
    color: "#ede4cf",
    font: "11px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace",
    zIndex: "9999",
    pointerEvents: "auto",
  } as CSSStyleDeclaration);
  document.body.appendChild(overlay);
  refreshOverlay();
}

function refreshOverlay(): void {
  if (!overlay) return;
  const rows: string[] = [];
  rows.push(`<div style="font-weight:bold;color:#f7c360;margin-bottom:6px">LAYOUT EDIT MODE — F2 to exit</div>`);
  rows.push(`<div style="color:#888;margin-bottom:8px">Cyan = group, magenta = cell. Drag body to move, gold handles to resize.</div>`);

  let lastParent: HTMLElement | null = null;
  for (const g of groups) {
    if (g.parent !== lastParent) {
      lastParent = g.parent;
      const parentClass = (g.parent.className || g.parent.id || g.parent.tagName).split(" ")[0];
      rows.push(`<div style="font-weight:bold;color:#6db3ff;margin:8px 0 4px">parent: .${parentClass}</div>`);
    }
    const parentBox = g.parent.getBoundingClientRect();
    const elBox = g.el.getBoundingClientRect();
    const left = ((elBox.left - parentBox.left) / parentBox.width) * 100;
    const top  = ((elBox.top  - parentBox.top)  / parentBox.height) * 100;
    const w    = (elBox.width  / parentBox.width)  * 100;
    const h    = (elBox.height / parentBox.height) * 100;
    const isCell = g.el.classList.contains("mp-layout-edit-cell");
    const accent = isCell ? "#e85cff" : "#f7c360";
    const indent = isCell ? "margin-left:14px;" : "";
    rows.push(
      `<div style="margin-bottom:4px;padding:3px 6px;background:rgba(247,195,96,0.04);border-left:2px solid ${accent};${indent}">` +
      `<div style="color:${accent};font-size:11px">${g.label}</div>` +
      `<div style="color:#aaa;font-size:10px">${g.selector}</div>` +
      `<div style="color:#ede4cf;font-variant-numeric:tabular-nums;font-size:10px">L ${left.toFixed(2)}% T ${top.toFixed(2)}% W ${w.toFixed(2)}% H ${h.toFixed(2)}%</div>` +
      `</div>`
    );
  }
  rows.push(`<button id="mp-layout-copy" style="width:100%;padding:6px;background:#2a3a4a;border:1px solid #4a5a6a;color:#ede4cf;border-radius:4px;cursor:pointer;margin-top:8px;font:inherit">Copy CSS to clipboard</button>`);
  overlay.innerHTML = rows.join("");

  const copyBtn = overlay.querySelector<HTMLButtonElement>("#mp-layout-copy");
  copyBtn?.addEventListener("click", () => {
    const css = generateCss();
    navigator.clipboard.writeText(css).then(() => {
      copyBtn!.textContent = "Copied! ✓";
      setTimeout(() => { copyBtn!.textContent = "Copy CSS to clipboard"; }, 1500);
    });
    console.log("[layout-editor] CSS:\n" + css);
  });
}

function generateCss(): string {
  const lines: string[] = ["/* Generated by layout-editor (F2). Paste into style.css. */"];
  for (const g of groups) {
    const parentBox = g.parent.getBoundingClientRect();
    const elBox = g.el.getBoundingClientRect();
    const left = ((elBox.left - parentBox.left) / parentBox.width) * 100;
    const top  = ((elBox.top  - parentBox.top)  / parentBox.height) * 100;
    const w    = (elBox.width  / parentBox.width)  * 100;
    const h    = (elBox.height / parentBox.height) * 100;
    lines.push(`${g.cssSelector} {`);
    lines.push(`  position: absolute;`);
    lines.push(`  left: ${left.toFixed(2)}%;`);
    lines.push(`  top: ${top.toFixed(2)}%;`);
    lines.push(`  width: ${w.toFixed(2)}%;`);
    lines.push(`  height: ${h.toFixed(2)}%;`);
    lines.push(`}`);
  }
  return lines.join("\n");
}
